import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Page'
import { SelectField } from '@/components/ui/Field'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import {
  useAssignSubscription,
  useSettings,
  useStudentSubscription,
  useSubscriptionPlans,
} from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import type { Student } from '@/types/api'
import styles from './SubscriptionDialog.module.css'

interface Props {
  student: Student | null
  onClose: () => void
}

/**
 * A student's subscription: what they hold, what they have spent, and why.
 *
 * *** THE LEDGER IS THE POINT OF THIS SCREEN ***
 * "I bought 12 sessions, where did they go?" is the question support gets, and
 * a balance with no explanation is what makes a paid product feel broken. The
 * counters answer "how many"; the ledger answers "why".
 */
export function SubscriptionDialog({ student, onClose }: Props) {
  const toast = useToast()
  const plans = useSubscriptionPlans()
  const settings = useSettings()
  const detail = useStudentSubscription(student?.user_id ?? null)
  const assignMutation = useAssignSubscription()

  const [planId, setPlanId] = useState('')

  if (!student) return null

  const activePlans = (plans.data?.plans ?? []).filter((p) => p.is_active)

  // Only once settings have loaded — see PlansPage for the same guard.
  const isPerSession = settings.data?.billing_mode === 'PER_SESSION'
  const subscription = detail.data?.subscription ?? null
  const ledger = detail.data?.ledger ?? []

  /*
   * *** "NO SUBSCRIPTION" AND "COULD NOT LOAD" MUST NOT LOOK THE SAME ***
   * The server 404s when a student has no plan, which is a normal state. Any
   * other failure means the balance on screen is unknown — showing the same
   * calm "no subscription yet" copy would state something we cannot verify,
   * and an admin would go on to assign a second plan over an existing one.
   */
  const notFound = detail.error instanceof ApiError && detail.error.status === 404
  const loadFailed = detail.error !== null && !notFound

  const assign = async () => {
    if (!planId) {
      toast.error('Choose a plan first.')
      return
    }
    try {
      await assignMutation.mutateAsync({
        studentId: student.user_id,
        planId,
      })
      toast.success(`Plan assigned to ${student.full_name}.`)
      setPlanId('')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not assign the plan.',
      )
    }
  }


  return (
    <Modal
      open
      onClose={onClose}
      title="Subscription"
      description={student.full_name}
      size="lg"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {loadFailed && (
          <Callout tone="danger" title="Could not load this subscription">
            {detail.error instanceof ApiError
              ? detail.error.message
              : 'Something went wrong.'}{' '}
            Assigning a plan is hidden until it loads, so an existing one is not
            replaced by mistake.
          </Callout>
        )}

        {/* Same reasoning as the Plans page: the action stays available so
            plans can be assigned before a switch, but an admin at a
            per-session client is told why it does nothing yet. */}
        {!loadFailed && isPerSession && (
          <Callout tone="warning" title="This organisation bills per session">
            Students are charged for each booking, so a plan assigned here is
            not in use yet. It takes effect only once the billing model is
            switched to subscription, which is done by the platform team.
          </Callout>
        )}

        {!loadFailed && !isPerSession && activePlans.length === 0 && (
          <Callout tone="info" title="No plans available">
            Create a subscription plan before assigning one to a student.
          </Callout>
        )}

        {/* ---------------------------------------------- current holding */}
        {subscription ? (
          <>
            <div className={styles.summary}>
              <div className={styles.planLine}>
                <span className={styles.planName}>
                  {subscription.plan_name ?? 'Plan'}
                </span>
                {subscription.is_live ? (
                  <Pill tone="success">Active</Pill>
                ) : (
                  <Pill tone="neutral">Not usable</Pill>
                )}
              </div>

              {/* Three numbers, because "remaining" alone hides the reason it
                  is lower than expected — held sessions are the usual cause. */}
              <div className={styles.stats}>
                <Stat label="Remaining" value={subscription.sessions_remaining} strong />
                <Stat label="Used" value={subscription.sessions_used} />
                <Stat
                  label="Held"
                  value={subscription.sessions_held}
                  hint="Booked but not yet completed"
                />
                <Stat label="Total" value={subscription.session_count} />
              </div>

              <p className={styles.meta}>
                {subscription.expires_at
                  ? `Expires ${formatDate(subscription.expires_at)}`
                  : 'No expiry — lasts until the sessions run out'}
              </p>
            </div>

            {/* ------------------------------------------------ adjustment */}

            {/* --------------------------------------------------- history */}
            <div>
              <p className={styles.sectionTitle}>History</p>
              {ledger.length === 0 ? (
                <p className={styles.muted}>Nothing recorded yet.</p>
              ) : (
                <ul className={styles.ledger}>
                  {ledger.map((entry) => (
                    <li key={entry.entry_id} className={styles.entry}>
                      <span className={styles.entryType}>
                        {ENTRY_LABELS[entry.entry_type] ?? entry.entry_type}
                      </span>
                      <span className={styles.entryReason}>
                        {entry.reason ?? '—'}
                      </span>
                      <span
                        className={
                          entry.delta > 0
                            ? styles.deltaUp
                            : entry.delta < 0
                              ? styles.deltaDown
                              : styles.deltaNone
                        }
                      >
                        {entry.delta > 0 ? `+${entry.delta}` : entry.delta || '·'}
                      </span>
                      <span className={styles.entryDate}>
                        {formatDate(entry.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          !detail.isLoading &&
          notFound && (
            <p className={styles.muted}>
              This student has no subscription yet.
            </p>
          )
        )}

        {/* ------------------------------------------------ assign a plan */}
        {!loadFailed && activePlans.length > 0 && (
          <div className={styles.assign}>
            <p className={styles.sectionTitle}>
              {subscription ? 'Assign a different plan' : 'Assign a plan'}
            </p>
            {subscription?.is_live && (
              // Worth saying: assigning again does not merge balances.
              <p className={styles.muted}>
                Assigning a new plan starts a fresh allowance. The current one
                stops being used.
              </p>
            )}
            <div className={styles.assignRow}>
              <SelectField
                label="Plan"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                options={[
                  { value: '', label: 'Choose a plan…' },
                  ...activePlans.map((p) => ({
                    value: p.plan_id,
                    label: `${p.name} — ${p.session_count} sessions`,
                  })),
                ]}
              />
              <Button
                variant="primary"
                onClick={() => void assign()}
                loading={assignMutation.isPending}
              >
                Assign
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function Stat({
  label,
  value,
  strong = false,
  hint,
}: {
  label: string
  value: number
  strong?: boolean
  hint?: string
}) {
  return (
    <div className={styles.stat} title={hint}>
      <span className={styles.statLabel}>{label}</span>
      <span className={strong ? styles.statValueStrong : styles.statValue}>
        {value}
      </span>
    </div>
  )
}

const ENTRY_LABELS: Record<string, string> = {
  GRANT: 'Granted',
  HOLD: 'Booked',
  RELEASE: 'Returned',
  CONSUME: 'Completed',
  ADJUST: 'Adjusted',
}
